import { useState, useEffect } from 'react';
import { setDataOnGrid, applySearch } from '../utils/views/pages/Dynamics';

export const useTableData = ({
    fetchFunction,
    deleteFunction,
    params = {},
    searchFields = [],
    dataKey, // Ex: 'apps', 'coupons', etc.
    initialItemsPerPage = process.env.REACT_APP_PAGE_ITEMS
}) => {
    // Estados comuns a todas as tabelas
    const [data, setData] = useState([]);
    const [dataBackup, setDataBackup] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [modalTitle, setModalTitle] = useState('');
    const [modalResult, setModalResult] = useState(false);
    const [modalOperation, setModalOperation] = useState('');
    const [reloadData, setReloadData] = useState(false);
    const [deletedId, setDeletedId] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchKey, setSearchKey] = useState('');
    const [foundRegisters, setFoundRegisters] = useState(0);
    const [loadingId, setLoadingId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [searchAction, setSearchAction] = useState(false)

    // Função de ordenação reutilizável
    /**
     * Ordena os dados do grupo de acordo com a coluna e direção
     * escolhida pela usuário
     * 
     * @param {*} dataToSort 
     * @param {*} field 
     * @param {*} direction 
     * @returns 
     */
    const sortData = (dataToSort, field, direction) => {
        if (!field) return dataToSort;

        const getNestedValue = (obj, path) => {
            return path.split('.').reduce((acc, part) => acc && acc[part], obj);
        };

        return [...dataToSort].sort((a, b) => {
            let aValue = getNestedValue(a, field) || '';
            let bValue = getNestedValue(b, field) || '';

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (direction === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    };

    const applySorting = (dataToSort) => {
        if (!sortField) return dataToSort;
        return sortData(dataToSort, sortField, sortDirection);
    };

    /**
     * Carregamento dos dados principais para alimentar o grid 
     * Alimenta os objetos que permitirão realizar buscas e filtragens
     * dinamicamente
     */
    useEffect(() => {
        const runAsync = async () => {
            setIsLoading(true);

            try {
                const response = await fetchFunction(params);
                const { data: responseData } = response;

                if (responseData.success) {
                    const fetchedData = responseData[dataKey];
                    setFoundRegisters(fetchedData?.length || 0);

                    const sortedData = applySorting(fetchedData);
                    setDataOnGrid(sortedData, setData, currentPage, itemsPerPage);
                    setDataBackup(fetchedData);
                    setReloadData(false);
                    setLoadingId('');
                }
            } catch (error) {
                console.error('Erro ao carregar dados:', error);
            } finally {
                setIsLoading(false);
            }
        };

        runAsync();
    }, [reloadData]);

    // Efeito para deletar itens
    /**
     * Trata ações realizadas, como a deleção de objetos
     */
    useEffect(() => {
        if (modalResult && modalOperation === 'delete' && deleteFunction) {
            const runAsync = async () => {

                setLoadingId(deletedId);
                const response = await deleteFunction(deletedId);
                const { data: responseData } = response;

                if (responseData.success) {
                    setReloadData(true);
                    setModalResult(false);
                    setDeletedId('');
                }
                
            };

            runAsync();
        }
    }, [modalResult, deleteFunction]);

    /**
     * Gatilho que captura situações onde devemos realizar busca de dados de acordo com 
     * filtro e reordenar os dados para mostrar no grid.
     * Esse UseEffect não deve ser executado se sua chamada tiver
     * se originado de uma busca feita pelo usuário na interface
     */
    useEffect(() => {
        if(!searchAction){
            applySearch(searchKey, searchFields, setData, dataBackup, setFoundRegisters, currentPage, itemsPerPage, true);
            const sortedData = applySorting(dataBackup);
            setDataOnGrid(sortedData, setData, currentPage, itemsPerPage);            
        } else {
            setSearchAction(false)
        }
    }, [sortField, sortDirection, searchKey, currentPage, itemsPerPage]);

    // Handlers reutilizáveis
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setCurrentPage(1);
    };

    const handleSearch = (e) => {
        setSearchAction(true)
        setSearchKey(e.target.value);
        applySearch(e.target.value, searchFields, setData, dataBackup, setFoundRegisters, currentPage, itemsPerPage, true);        
    };

    const handleDelete = (id, entityName = 'item') => {
        setModalOperation('delete');
        setModalTitle(`${entityName} Deletion`);
        setModalMessage(`Are you sure you want to delete this ${entityName.toLowerCase()}?`);
        setShowModal(true);
        setDeletedId(id);
    };

    return {
        // Estados
        data,
        dataBackup,
        showModal,
        modalMessage,
        modalTitle,
        modalResult,
        modalOperation,
        loadingId,
        isLoading,
        foundRegisters,
        currentPage,
        itemsPerPage,
        searchKey,
        sortField,
        sortDirection,

        // Setters
        setShowModal,
        setModalResult,
        setCurrentPage,

        // Handlers
        handleSort,
        handleSearch,
        handleDelete,

        // Funções auxiliares
        sortData,
        applySorting
    };
};